import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft, Dumbbell, Flame, Save, Plus, X, Clock, Calendar,
  ChevronRight, Zap, Target, Layers, Search, Trash2, Play,
  Edit, Check, ChevronDown, ChevronUp, TrendingUp, Trophy, Users, FolderOpen, FolderPlus, MoreVertical
} from 'lucide-react';

const TIPO_CONFIG = {
  tren_superior: { label: 'Tren Superior', desc: 'Pecho, espalda, hombros, brazos', icon: <Zap className="w-5 h-5" />, color: 'from-blue-600 to-cyan-500', accent: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/10' },
  tren_inferior: { label: 'Tren Inferior', desc: 'Piernas, gluteos, gemelos', icon: <Target className="w-5 h-5" />, color: 'from-orange-600 to-red-500', accent: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10' },
  full_body: { label: 'Full Body', desc: 'Cuerpo completo', icon: <Layers className="w-5 h-5" />, color: 'from-purple-600 to-pink-500', accent: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/10' },
};

// ---- Buscador de ejercicios ----
function ExerciseSearch({ tipo, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [customName, setCustomName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (tipo) params.set('tipo', tipo);
        const res = await axios.get(`${API}/catalogo-ejercicios?${params}`);
        setResults(res.data);
      } catch (e) { console.error(e); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, tipo]);

  const createCustom = async () => {
    if (!customName.trim()) return;
    try {
      const res = await axios.post(`${API}/catalogo-ejercicios`, {
        nombre: customName.trim(),
        grupo_muscular: 'otro',
        tipos_sesion: ['tren_superior', 'tren_inferior', 'full_body']
      });
      onSelect(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-gray-900 w-full max-w-lg max-h-[80vh] rounded-t-2xl sm:rounded-2xl flex flex-col" onClick={e => e.stopPropagation()} data-testid="exercise-search-modal">
        <div className="p-4 border-b border-gray-800 space-y-3">
          <div className="flex items-center gap-2 bg-gray-800 rounded-xl px-3">
            <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Buscar ejercicio..." className="w-full py-3 bg-transparent text-white outline-none text-sm"
              data-testid="search-exercise-input" />
            {query && <button onClick={() => setQuery('')} className="text-gray-500"><X className="w-4 h-4" /></button>}
          </div>
          {showCreate ? (
            <div className="flex gap-2">
              <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Nombre del ejercicio..."
                className="bg-gray-800 border-gray-700 text-white text-sm" autoFocus
                onKeyDown={e => e.key === 'Enter' && createCustom()} data-testid="create-exercise-input" />
              <Button onClick={createCustom} className="bg-gold text-black hover:bg-gold/80 text-sm font-bold px-4" data-testid="btn-create-exercise">
                <Check className="w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={() => { setShowCreate(false); setCustomName(''); }} className="text-gray-400 px-2">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button onClick={() => setShowCreate(true)}
              className="w-full py-2 text-sm text-gold flex items-center justify-center gap-2 bg-gold/10 hover:bg-gold/20 rounded-lg transition-colors border border-gold/20"
              data-testid="btn-show-create-exercise">
              <Plus className="w-4 h-4" /> Crear ejercicio manual
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {results.map(ej => (
            <button key={ej.id} onClick={() => onSelect(ej)}
              className="w-full text-left px-4 py-3 hover:bg-gray-800 rounded-lg flex items-center gap-3 transition-colors"
              data-testid={`search-result-${ej.id}`}>
              <Dumbbell className="w-4 h-4 text-gold flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{ej.nombre}</p>
                <p className="text-gray-600 text-xs">{ej.grupo_muscular}{!ej.es_sistema ? ' (personalizado)' : ''}</p>
              </div>
            </button>
          ))}
          {results.length === 0 && query && (
            <p className="text-center text-gray-600 py-6 text-sm">Sin resultados para "{query}"</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Progression Chart (SVG) ----
function ProgressionChart({ data, record }) {
  if (!data || data.length === 0) return <p className="text-gray-600 text-sm text-center py-6">Sin datos de progresion</p>;
  const W = 320, H = 150, PAD_L = 40, PAD_R = 10, PAD_T = 15, PAD_B = 30;
  const gW = W - PAD_L - PAD_R, gH = H - PAD_T - PAD_B;
  const maxP = Math.max(...data.map(d => d.peso_max), 1);
  const minP = Math.min(...data.map(d => d.peso_max));
  const range = maxP - minP || 1;
  const yMin = Math.max(0, minP - range * 0.15);
  const yMax = maxP + range * 0.15;
  const yRange = yMax - yMin || 1;

  const points = data.map((d, i) => {
    const x = PAD_L + (data.length === 1 ? gW / 2 : (i / (data.length - 1)) * gW);
    const y = PAD_T + gH - ((d.peso_max - yMin) / yRange) * gH;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = linePath + ` L${points[points.length - 1].x},${PAD_T + gH} L${points[0].x},${PAD_T + gH} Z`;
  const gridLines = 4;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="progression-chart">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAB308" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#EAB308" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {Array.from({ length: gridLines }).map((_, i) => {
        const y = PAD_T + (i / (gridLines - 1)) * gH;
        const val = yMax - (i / (gridLines - 1)) * yRange;
        return (
          <g key={i}>
            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#1f2937" strokeWidth="0.5" />
            <text x={PAD_L - 4} y={y + 3} textAnchor="end" fill="#6b7280" fontSize="8">{Math.round(val)}</text>
          </g>
        );
      })}
      {data.length > 1 && <path d={areaPath} fill="url(#areaGrad)" />}
      {data.length > 1 && <path d={linePath} fill="none" stroke="#EAB308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={p.es_record ? 5 : 3.5} fill={p.es_record ? '#f97316' : '#EAB308'} stroke="#0f0f10" strokeWidth="1.5" />
          <text x={p.x} y={PAD_T + gH + 14} textAnchor="middle" fill="#6b7280" fontSize="7">
            {p.fecha.slice(5)}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ---- Main Component ----
function SesionesFuerza() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.es_pro || user?.role === 'entrenador';
  const isEntrenador = user?.role === 'entrenador';

  const [view, setView] = useState('home');
  // home | crear | entrenar | historial | detalle | progresion | progresion_detalle | asignar
  const [rutinas, setRutinas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  // Crear rutina
  const [crearTipo, setCrearTipo] = useState('');
  const [crearNombre, setCrearNombre] = useState('');
  const [crearEjercicios, setCrearEjercicios] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [editingRutina, setEditingRutina] = useState(null);

  // Entrenar
  const [rutinaActiva, setRutinaActiva] = useState(null);
  const [setsData, setSetsData] = useState({});
  const [prevData, setPrevData] = useState({});
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);

  // Detalle
  const [detalle, setDetalle] = useState(null);

  // Progresion
  const [ejerciciosConDatos, setEjerciciosConDatos] = useState([]);
  const [progresionData, setProgresionData] = useState(null);
  const [progresionLoading, setProgresionLoading] = useState(false);

  // Asignacion (solo entrenador)
  const [asignarRutina, setAsignarRutina] = useState(null);
  const [clientPlusUsers, setClientPlusUsers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [asignarNotas, setAsignarNotas] = useState('');
  const [asignando, setAsignando] = useState(false);

  // Carpetas
  const [carpetas, setCarpetas] = useState([]);
  const [activeCarpeta, setActiveCarpeta] = useState('todas'); // 'todas' | 'sin_clasificar' | carpeta_id
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderMenuId, setFolderMenuId] = useState(null);
  const [manageFoldersRutina, setManageFoldersRutina] = useState(null);
  const [draggingRutinaId, setDraggingRutinaId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);

  useEffect(() => {
    if (!isPro) { navigate('/'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/rutinas-fuerza`),
        axios.get(`${API}/entrenamientos-fuerza`),
        axios.get(`${API}/carpetas-rutinas`)
      ]);
      setRutinas(r1.data);
      setHistorial(r2.data);
      setCarpetas(r3.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openProgresion = async () => {
    setView('progresion');
    try {
      const res = await axios.get(`${API}/ejercicios-con-datos`);
      setEjerciciosConDatos(res.data.filter(e => !e.nombre.startsWith('TEST_')));
    } catch (e) { console.error(e); }
  };

  const loadProgresionDetail = async (nombre) => {
    setProgresionLoading(true);
    setProgresionData(null);
    setView('progresion_detalle');
    try {
      const res = await axios.get(`${API}/progresion-fuerza/${encodeURIComponent(nombre)}`);
      setProgresionData(res.data);
    } catch (e) { console.error(e); }
    finally { setProgresionLoading(false); }
  };

  // ---- CREAR RUTINA ----
  const startCrear = (tipo) => {
    setCrearTipo(tipo);
    setCrearNombre('');
    setCrearEjercicios([]);
    setEditingRutina(null);
    setView('crear');
  };

  const startEditRutina = (rutina) => {
    setCrearTipo(rutina.tipo);
    setCrearNombre(rutina.nombre);
    setCrearEjercicios(rutina.ejercicios.map(ej => ({
      ejercicio_id: ej.ejercicio_id, nombre: ej.nombre, series: ej.series, reps: ej.reps
    })));
    setEditingRutina(rutina);
    setView('crear');
  };

  const addExercise = (ej) => {
    setCrearEjercicios(prev => [...prev, {
      ejercicio_id: ej.id, nombre: ej.nombre, series: 3, reps: 10
    }]);
    setShowSearch(false);
  };

  const updateCrearEj = (index, field, value) => {
    setCrearEjercicios(prev => prev.map((ej, i) =>
      i === index ? { ...ej, [field]: parseInt(value) || 0 } : ej
    ));
  };

  const removeCrearEj = (index) => {
    setCrearEjercicios(prev => prev.filter((_, i) => i !== index));
  };

  const moveCrearEj = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= crearEjercicios.length) return;
    setCrearEjercicios(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const saveRutina = async () => {
    if (!crearNombre.trim()) { alert('Pon un nombre a la rutina'); return; }
    if (crearEjercicios.length === 0) { alert('Agrega al menos un ejercicio'); return; }
    try {
      if (editingRutina) {
        await axios.put(`${API}/rutinas-fuerza/${editingRutina.id}`, {
          nombre: crearNombre, tipo: crearTipo, ejercicios: crearEjercicios
        });
      } else {
        await axios.post(`${API}/rutinas-fuerza`, {
          nombre: crearNombre, tipo: crearTipo, ejercicios: crearEjercicios
        });
      }
      await loadData();
      setView('home');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const deleteRutina = async (id) => {
    if (!window.confirm('Eliminar esta rutina?')) return;
    try {
      await axios.delete(`${API}/rutinas-fuerza/${id}`);
      await loadData();
    } catch (e) { alert('Error'); }
  };

  // ---- ASIGNAR RUTINA (solo entrenador) ----
  const openAsignar = async (rutina) => {
    setAsignarRutina(rutina);
    setSelectedClients([]);
    setAsignarNotas('');
    try {
      const res = await axios.get(`${API}/client-plus/users`);
      setClientPlusUsers(res.data);
    } catch (e) { console.error(e); }
    setView('asignar');
  };

  const handleAsignar = async () => {
    if (selectedClients.length === 0) { alert('Selecciona al menos un cliente'); return; }
    setAsignando(true);
    try {
      const res = await axios.post(`${API}/rutinas-fuerza/${asignarRutina.id}/asignar`, {
        client_ids: selectedClients,
        notas: asignarNotas
      });
      alert(`Rutina asignada a ${res.data.created} cliente(s)`);
      setView('home');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setAsignando(false);
  };

  const toggleClient = (id) => {
    setSelectedClients(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  // ---- CARPETAS ----
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/carpetas-rutinas`, { nombre: newFolderName.trim() });
      setCarpetas(prev => [...prev, res.data]);
      setNewFolderName('');
      setShowNewFolder(false);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const renameFolder = async (id, nombre) => {
    try {
      const res = await axios.put(`${API}/carpetas-rutinas/${id}`, { nombre });
      setCarpetas(prev => prev.map(c => c.id === id ? res.data : c));
      setEditingFolder(null);
    } catch (e) { alert('Error'); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('Eliminar carpeta? Las rutinas no se borran, solo pierden esta etiqueta.')) return;
    try {
      await axios.delete(`${API}/carpetas-rutinas/${id}`);
      setCarpetas(prev => prev.filter(c => c.id !== id));
      setRutinas(prev => prev.map(r => ({ ...r, carpetas: (r.carpetas || []).filter(c => c !== id) })));
      if (activeCarpeta === id) setActiveCarpeta('todas');
      setFolderMenuId(null);
    } catch (e) { alert('Error'); }
  };

  const toggleRutinaFolder = async (rutinaId, folderId) => {
    const rutina = rutinas.find(r => r.id === rutinaId);
    if (!rutina) return;
    const current = rutina.carpetas || [];
    const newCarpetas = current.includes(folderId) ? current.filter(c => c !== folderId) : [...current, folderId];
    try {
      await axios.put(`${API}/rutinas-fuerza/${rutinaId}/carpetas`, { carpetas: newCarpetas });
      setRutinas(prev => prev.map(r => r.id === rutinaId ? { ...r, carpetas: newCarpetas } : r));
    } catch (e) { alert('Error'); }
  };

  const addRutinaToFolder = async (rutinaId, folderId) => {
    const rutina = rutinas.find(r => r.id === rutinaId);
    if (!rutina) return;
    const current = rutina.carpetas || [];
    if (current.includes(folderId)) return;
    const newCarpetas = [...current, folderId];
    try {
      await axios.put(`${API}/rutinas-fuerza/${rutinaId}/carpetas`, { carpetas: newCarpetas });
      setRutinas(prev => prev.map(r => r.id === rutinaId ? { ...r, carpetas: newCarpetas } : r));
    } catch (e) { alert('Error'); }
  };

  const filteredRutinas = activeCarpeta === 'todas' ? rutinas
    : activeCarpeta === 'sin_clasificar' ? rutinas.filter(r => !(r.carpetas?.length))
    : rutinas.filter(r => (r.carpetas || []).includes(activeCarpeta));

  // ---- ENTRENAR ----
  const startEntrenar = async (rutina) => {
    setRutinaActiva(rutina);
    setRecords([]);
    // Init sets for each exercise
    const initial = {};
    rutina.ejercicios.forEach((ej, i) => {
      initial[i] = Array.from({ length: ej.series }, (_, s) => ({
        numero: s + 1, peso: '', reps: ej.reps
      }));
    });
    setSetsData(initial);
    // Load previous workout
    try {
      const res = await axios.get(`${API}/entrenamientos-fuerza/ultimo/${rutina.id}`);
      if (res.data) {
        const prev = {};
        res.data.ejercicios.forEach((ej, i) => {
          prev[i] = ej.sets || [];
        });
        setPrevData(prev);
      } else { setPrevData({}); }
    } catch { setPrevData({}); }
    setView('entrenar');
  };

  const updateSetPeso = (ejIndex, setIndex, value) => {
    setSetsData(prev => ({
      ...prev,
      [ejIndex]: prev[ejIndex].map((s, i) => i === setIndex ? { ...s, peso: value } : s)
    }));
  };

  const updateSetReps = (ejIndex, setIndex, value) => {
    setSetsData(prev => ({
      ...prev,
      [ejIndex]: prev[ejIndex].map((s, i) => i === setIndex ? { ...s, reps: parseInt(value) || 0 } : s)
    }));
  };

  const saveEntrenamiento = async () => {
    if (!rutinaActiva) return;
    setSaving(true);
    try {
      const ejercicios = rutinaActiva.ejercicios.map((ej, i) => ({
        nombre: ej.nombre,
        series_config: ej.series,
        reps_config: ej.reps,
        sets: (setsData[i] || []).map(s => ({
          numero: s.numero, peso: parseFloat(s.peso) || 0, reps: s.reps
        }))
      }));
      const res = await axios.post(`${API}/entrenamientos-fuerza`, {
        rutina_id: rutinaActiva.id,
        tipo: rutinaActiva.tipo,
        nombre_rutina: rutinaActiva.nombre,
        ejercicios
      });
      if (res.data.records_detalle?.length > 0) {
        setRecords(res.data.records_detalle);
      } else {
        setView('home');
      }
      await loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-dark-mk flex items-center justify-center"><div className="text-gold">Cargando...</div></div>;
  }

  const cfg = (tipo) => TIPO_CONFIG[tipo] || TIPO_CONFIG.full_body;

  // =============== ASIGNAR VIEW (Entrenador) ===============
  if (view === 'asignar' && asignarRutina && isEntrenador) {
    const ac = cfg(asignarRutina.tipo);
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="asignar-view">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('home')} className="text-gray-400 hover:text-white" data-testid="btn-back-asignar">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">Asignar Rutina</h1>
              <p className={`text-sm ${ac.accent}`}>{asignarRutina.nombre}</p>
            </div>
          </div>

          {clientPlusUsers.length === 0 ? (
            <div className="text-center py-8 bg-gray-900 rounded-xl border border-gray-800">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No hay clientes Plus disponibles</p>
              <p className="text-gray-500 text-xs mt-1">Primero asigna el rol "Cliente Plus" desde el panel de usuarios</p>
            </div>
          ) : (
            <>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Selecciona clientes</p>
              <div className="space-y-2">
                {clientPlusUsers.map(c => (
                  <button key={c.id} onClick={() => toggleClient(c.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      selectedClients.includes(c.id) 
                        ? 'bg-gold/10 border-gold/40' 
                        : 'bg-gray-900 border-gray-800 hover:border-gray-600'
                    }`}
                    data-testid={`client-select-${c.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      selectedClients.includes(c.id) ? 'bg-gold text-black' : 'bg-gray-700 text-gray-300'
                    }`}>
                      {selectedClients.includes(c.id) ? <Check className="w-4 h-4" /> : c.nombre[0]}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-white text-sm font-medium">{c.nombre}</p>
                      <p className="text-gray-500 text-xs">{c.email}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Notas (opcional)</p>
                <Input value={asignarNotas} onChange={e => setAsignarNotas(e.target.value)}
                  placeholder="Ej: Hacer 3 veces por semana..."
                  className="bg-gray-900 border-gray-800 text-white"
                  data-testid="input-asignar-notas"
                />
              </div>

              <Button onClick={handleAsignar} disabled={asignando || selectedClients.length === 0}
                className="w-full bg-gold text-black font-bold hover:bg-gold/80 py-3"
                data-testid="btn-confirm-asignar"
              >
                {asignando ? 'Asignando...' : `Asignar a ${selectedClients.length} cliente(s)`}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // =============== PROGRESION DETALLE VIEW ===============
  if (view === 'progresion_detalle') {
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="progresion-detalle-view">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('progresion')} className="text-gray-400 hover:text-white" data-testid="btn-back-prog-det">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-white">{progresionData?.nombre || 'Cargando...'}</h1>
              <p className="text-gray-500 text-sm">Progresion de peso</p>
            </div>
          </div>
          {progresionLoading ? (
            <div className="text-center py-12 text-gray-500">Cargando...</div>
          ) : progresionData && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center">
                  <Trophy className="w-5 h-5 text-gold mx-auto mb-1" />
                  <p className="text-gold text-xl font-black font-mono">{progresionData.record_peso}</p>
                  <p className="text-gray-500 text-[10px]">RECORD (kg)</p>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center">
                  <Calendar className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <p className="text-white text-xl font-black font-mono">{progresionData.total_sesiones}</p>
                  <p className="text-gray-500 text-[10px]">SESIONES</p>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center">
                  <Dumbbell className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                  <p className="text-white text-xl font-black font-mono">{progresionData.total_registros}</p>
                  <p className="text-gray-500 text-[10px]">REGISTROS</p>
                </div>
              </div>
              {/* Chart */}
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-xs font-medium uppercase mb-3">Peso maximo por sesion</p>
                  <ProgressionChart data={progresionData.progresion} record={progresionData.record_peso} />
                </CardContent>
              </Card>
              {/* Session list */}
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Detalle por sesion</p>
              {progresionData.progresion.map((p, i) => (
                <div key={i} className={`p-3 rounded-xl border ${p.es_record ? 'bg-gold/5 border-gold/30' : 'bg-gray-900 border-gray-800'}`} data-testid={`prog-session-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium">{new Date(p.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                      <p className="text-gray-500 text-xs">{p.rutina}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-black font-mono ${p.es_record ? 'text-gold' : 'text-white'}`}>{p.peso_max}kg</span>
                      {p.es_record && <Flame className="w-4 h-4 text-orange-400" />}
                    </div>
                  </div>
                  {p.sets.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {p.sets.map((s, j) => (
                        <span key={j} className={`text-xs px-2 py-0.5 rounded font-mono ${s.peso === p.peso_max ? 'bg-gold/20 text-gold' : 'bg-gray-800 text-gray-400'}`}>
                          S{s.numero}: {s.peso}kg
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  // =============== PROGRESION LIST VIEW ===============
  if (view === 'progresion') {
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="progresion-view">
        <div className="max-w-lg mx-auto space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('home')} className="text-gray-400 hover:text-white" data-testid="btn-back-prog">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div><h1 className="text-2xl font-bold text-gold flex items-center gap-2"><TrendingUp className="h-6 w-6" /> Progresion</h1><p className="text-gray-500 text-sm">{ejerciciosConDatos.length} ejercicios con datos</p></div>
          </div>
          {ejerciciosConDatos.length === 0 ? (
            <div className="text-center py-16"><TrendingUp className="w-16 h-16 text-gray-700 mx-auto mb-3" /><p className="text-gray-500">Entrena para ver tu progresion</p></div>
          ) : ejerciciosConDatos.map((ej, i) => (
            <div key={ej.id || i} onClick={() => loadProgresionDetail(ej.nombre)}
              className="flex items-center gap-3 p-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 cursor-pointer transition-all group" data-testid={`prog-ej-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Dumbbell className="w-5 h-5 text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">{ej.nombre}</p>
                <p className="text-gray-500 text-xs">{ej.total_registros} registros</p>
              </div>
              <div className="text-right">
                <p className="text-gold font-black font-mono text-lg">{ej.record_peso}<span className="text-xs text-gray-500">kg</span></p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-700 group-hover:text-gold transition-colors" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // =============== DETALLE VIEW ===============
  if (view === 'detalle' && detalle) {
    const c = cfg(detalle.tipo);
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="detalle-view">
        <div className="max-w-lg mx-auto space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('historial')} className="text-gray-400 hover:text-white" data-testid="btn-back-detalle">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-white">{detalle.nombre_rutina}</h1>
              <p className="text-gray-500 text-sm">{new Date(detalle.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
            {detalle.records_rotos > 0 && (
              <div className="flex items-center gap-1 bg-gold/20 px-2 py-1 rounded-full">
                <Flame className="w-4 h-4 text-gold" /><span className="text-gold text-xs font-bold">{detalle.records_rotos}</span>
              </div>
            )}
          </div>
          {detalle.ejercicios.map((ej, i) => (
            <div key={i} className={`p-4 rounded-xl border ${ej.es_record ? 'bg-gold/5 border-gold/30' : 'bg-gray-900 border-gray-800'}`} data-testid={`det-ej-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-bold">{ej.nombre}</p>
                {ej.es_record && <Flame className="w-4 h-4 text-orange-400" />}
              </div>
              <p className="text-gray-500 text-xs mb-2">{ej.series_config}x{ej.reps_config} &middot; max {ej.peso_max} kg</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ej.sets.map((s, j) => (
                  <div key={j} className="bg-gray-800 rounded-lg p-2 text-center">
                    <p className="text-gray-500 text-[10px]">Serie {s.numero}</p>
                    <p className={`text-lg font-black font-mono ${s.peso === ej.peso_max && ej.es_record ? 'text-gold' : 'text-white'}`}>{s.peso}<span className="text-xs text-gray-500">kg</span></p>
                    <p className="text-gray-600 text-[10px]">{s.reps} reps</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // =============== HISTORIAL VIEW ===============
  if (view === 'historial') {
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="historial-view">
        <div className="max-w-lg mx-auto space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('home')} className="text-gray-400 hover:text-white" data-testid="btn-back-historial">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div><h1 className="text-2xl font-bold text-gold">Historial</h1><p className="text-gray-500 text-sm">{historial.length} entrenamientos</p></div>
          </div>
          {historial.length === 0 ? (
            <div className="text-center py-16"><Calendar className="w-16 h-16 text-gray-700 mx-auto mb-3" /><p className="text-gray-500">Sin entrenamientos aun</p></div>
          ) : historial.map(e => {
            const c = cfg(e.tipo);
            return (
              <div key={e.id} onClick={() => { setDetalle(e); setView('detalle'); }}
                className="flex items-center gap-3 p-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 cursor-pointer transition-all group" data-testid={`hist-${e.id}`}>
                <div className={`w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0 ${c.accent}`}>{c.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm">{e.nombre_rutina}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{new Date(e.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                    <span>{e.total_ejercicios} ej.</span>
                    {e.records_rotos > 0 && <span className="flex items-center gap-1 text-gold"><Flame className="w-3 h-3" />{e.records_rotos}</span>}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-700 group-hover:text-gold transition-colors" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // =============== ENTRENAR VIEW ===============
  if (view === 'entrenar' && rutinaActiva) {
    const c = cfg(rutinaActiva.tipo);
    if (records.length > 0) {
      return (
        <div className="min-h-screen bg-dark-mk flex items-center justify-center p-4" data-testid="records-screen">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="bg-gradient-to-br from-orange-600 via-red-600 to-yellow-600 rounded-2xl p-8 shadow-2xl shadow-orange-600/20">
              <Flame className="w-16 h-16 text-yellow-300 mx-auto mb-3 animate-bounce" />
              <p className="text-white text-3xl font-black mb-4">NUEVO{records.length > 1 ? 'S' : ''} RECORD{records.length > 1 ? 'S' : ''}!</p>
              {records.map((r, i) => (
                <div key={i} className="bg-white/95 rounded-xl p-3 mb-2">
                  <p className="text-gray-900 font-bold uppercase tracking-wide text-sm">{r.nombre}</p>
                  <p className="text-orange-600 font-mono text-xl font-black">{r.peso} kg <span className="text-gray-500 text-sm font-normal">anterior: {r.anterior || 0} kg</span></p>
                </div>
              ))}
            </div>
            <Button onClick={() => { setRecords([]); setView('home'); }} className="w-full h-12 bg-gold text-black font-bold text-lg hover:bg-gold/80" data-testid="btn-close-records">
              Continuar
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-32" data-testid="entrenar-view">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('home')} className="text-gray-400 hover:text-white" data-testid="btn-back-entrenar">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className={`text-xl font-bold ${c.accent}`}>{rutinaActiva.nombre}</h1>
              <p className="text-gray-500 text-sm">{new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
          </div>

          {rutinaActiva.ejercicios.map((ej, ejIdx) => {
            const prevSets = prevData[ejIdx] || [];
            return (
              <Card key={ejIdx} className="bg-gray-900 border-gray-800" data-testid={`train-ej-${ejIdx}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-bold">{ej.nombre}</p>
                      <p className="text-gray-500 text-xs">{ej.series} series x {ej.reps} reps</p>
                    </div>
                    <Dumbbell className="w-5 h-5 text-gray-700" />
                  </div>
                  {/* Header row */}
                  <div className="flex items-center gap-2 mb-1 px-1">
                    <span className="text-gray-500 text-[10px] uppercase w-12 flex-shrink-0">Serie</span>
                    <span className="text-gray-500 text-[10px] uppercase text-center flex-1">Peso (kg)</span>
                    <span className="text-gray-500 text-[10px] uppercase text-center w-16">Reps</span>
                    <span className="text-gray-500 text-[10px] uppercase text-right w-20">Prev</span>
                  </div>
                  <div className="space-y-2">
                    {(setsData[ejIdx] || []).map((set, sIdx) => {
                      const prev = prevSets[sIdx];
                      const prevPeso = prev?.peso || 0;
                      const prevReps = prev?.reps || 0;
                      const curPeso = parseFloat(set.peso) || 0;
                      const isBeating = prevPeso > 0 && curPeso > prevPeso;
                      return (
                        <div key={sIdx} className="flex items-center gap-2" data-testid={`set-${ejIdx}-${sIdx}`}>
                          <span className="text-gray-500 text-xs w-12 flex-shrink-0">S{set.numero}</span>
                          <Input type="number" value={set.peso} onChange={e => updateSetPeso(ejIdx, sIdx, e.target.value)}
                            placeholder="0" min="0" step="0.5"
                            className={`bg-gray-800 border-gray-700 text-white text-center h-10 font-mono flex-1 ${isBeating ? 'border-green-500 text-green-400' : ''}`}
                            data-testid={`input-peso-${ejIdx}-${sIdx}`} />
                          <Input type="number" value={set.reps} onChange={e => updateSetReps(ejIdx, sIdx, e.target.value)}
                            min="1"
                            className="bg-gray-800 border-gray-700 text-white text-center h-10 font-mono w-16"
                            data-testid={`input-reps-${ejIdx}-${sIdx}`} />
                          {prevPeso > 0 ? (
                            <span className={`text-xs w-20 text-right font-mono leading-tight ${isBeating ? 'text-green-400' : 'text-gray-500'}`}>{prevPeso}kg<br/><span className="text-gray-600">{prevReps}r</span></span>
                          ) : (
                            <span className="text-xs w-20 text-right text-gray-500">--</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[var(--v-bg,#0A0F0D)] via-[var(--v-bg,#0A0F0D)] to-transparent">
            <div className="max-w-lg mx-auto">
              <Button onClick={saveEntrenamiento} disabled={saving}
                className="w-full h-14 bg-gradient-to-r from-gold to-yellow-500 text-black hover:from-gold/90 hover:to-yellow-400 font-black text-lg disabled:opacity-30 shadow-lg shadow-gold/20"
                data-testid="btn-save-entrenamiento">
                {saving ? <span className="flex items-center gap-2"><Clock className="w-5 h-5 animate-spin" /> Guardando...</span>
                  : <span className="flex items-center gap-2"><Save className="w-5 h-5" /> Guardar Entrenamiento</span>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =============== CREAR/EDITAR RUTINA VIEW ===============
  if (view === 'crear') {
    const c = cfg(crearTipo);
    return (
      <div className="min-h-screen bg-dark-mk p-4 pb-6" data-testid="crear-view">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView('home')} className="text-gray-400 hover:text-white" data-testid="btn-back-crear">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className={`text-xl font-bold ${c.accent}`}>{editingRutina ? 'Editar Rutina' : 'Nueva Rutina'}</h1>
          </div>

          <div className={`p-3 rounded-xl ${c.bg} ${c.border} border flex items-center gap-2`}>
            <span className={c.accent}>{c.icon}</span>
            <span className={`text-sm font-medium ${c.accent}`}>{c.label}</span>
          </div>

          <Input value={crearNombre} onChange={e => setCrearNombre(e.target.value)}
            placeholder="Nombre de la rutina (ej: Mi dia de pecho)"
            className="bg-gray-900 border-gray-800 text-white h-12" data-testid="input-rutina-nombre" />

          {crearEjercicios.map((ej, i) => (
            <div key={i} className="bg-gray-900 rounded-xl border border-gray-800 p-3 flex items-center gap-2" data-testid={`crear-ej-${i}`}>
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button onClick={() => moveCrearEj(i, -1)} disabled={i === 0}
                  className={`p-0.5 rounded transition-colors ${i === 0 ? 'text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-gold'}`}
                  data-testid={`move-up-${i}`}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button onClick={() => moveCrearEj(i, 1)} disabled={i === crearEjercicios.length - 1}
                  className={`p-0.5 rounded transition-colors ${i === crearEjercicios.length - 1 ? 'text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-gold'}`}
                  data-testid={`move-down-${i}`}>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{ej.nombre}</p>
              </div>
              <div className="flex items-center gap-1">
                <Input type="number" value={ej.series} onChange={e => updateCrearEj(i, 'series', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white text-center w-14 h-8 text-xs" min="1" data-testid={`series-${i}`} />
                <span className="text-gray-500 text-xs">x</span>
                <Input type="number" value={ej.reps} onChange={e => updateCrearEj(i, 'reps', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white text-center w-14 h-8 text-xs" min="1" data-testid={`reps-${i}`} />
              </div>
              <button onClick={() => removeCrearEj(i)} className="text-gray-600 hover:text-red-400 p-1" data-testid={`remove-ej-${i}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button onClick={() => setShowSearch(true)}
            className="w-full py-3 border border-dashed border-gray-700 rounded-xl text-gray-400 hover:text-gold hover:border-gold/50 transition-colors flex items-center justify-center gap-2 text-sm"
            data-testid="btn-add-exercise">
            <Plus className="w-4 h-4" /> Agregar ejercicio
          </button>

          {showSearch && <ExerciseSearch tipo={crearTipo} onSelect={addExercise} onClose={() => setShowSearch(false)} />}

          <Button onClick={saveRutina} disabled={crearEjercicios.length === 0}
            className="w-full h-14 bg-gradient-to-r from-gold to-yellow-500 text-black hover:from-gold/90 hover:to-yellow-400 font-black text-lg disabled:opacity-30 shadow-lg shadow-gold/20"
            data-testid="btn-save-rutina">
            <Save className="w-5 h-5 mr-2" /> {editingRutina ? 'Actualizar Rutina' : 'Guardar Rutina'}
          </Button>
        </div>
      </div>
    );
  }

  // =============== HOME VIEW ===============
  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="sesiones-fuerza-home">
      <div className="max-w-lg mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white" data-testid="btn-back-home">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2"><Dumbbell className="h-8 w-8" /> Fuerza</h1>
            <p className="text-gray-500 text-sm">Crea rutinas y registra tu progreso</p>
          </div>
        </div>

        {/* Crear nueva rutina */}
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Nueva rutina</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(TIPO_CONFIG).map(([tipo, c]) => (
              <button key={tipo} onClick={() => startCrear(tipo)}
                className={`p-3 rounded-xl bg-gradient-to-br ${c.color} text-white text-center hover:scale-[1.03] active:scale-[0.97] transition-transform`}
                data-testid={`btn-crear-${tipo}`}>
                <div className="flex justify-center mb-1">{c.icon}</div>
                <p className="text-xs font-bold leading-tight">{c.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Mis rutinas con carpetas */}
        {rutinas.length > 0 && (
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Mis rutinas</p>

            {/* Folder tabs */}
            {draggingRutinaId && carpetas.length > 0 && (
              <p className="text-xs text-gold mb-1 animate-pulse" data-testid="drag-hint">Suelta en una carpeta para organizar</p>
            )}
            <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="folder-tabs">
              <button onClick={() => setActiveCarpeta('todas')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeCarpeta === 'todas' ? 'bg-gold/20 text-gold border border-gold/40' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                }`} data-testid="folder-todas">
                Todas ({rutinas.length})
              </button>
              <button onClick={() => setActiveCarpeta('sin_clasificar')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeCarpeta === 'sin_clasificar' ? 'bg-gray-600/30 text-white border border-gray-500' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                }`} data-testid="folder-sin-clasificar">
                Sin clasificar ({rutinas.filter(r => !(r.carpetas?.length)).length})
              </button>
              {carpetas.map(f => (
                <div key={f.id} className="relative flex-shrink-0">
                  <button onClick={() => setActiveCarpeta(f.id)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolderId(f.id); }}
                    onDragLeave={() => setDragOverFolderId(null)}
                    onDrop={(e) => { e.preventDefault(); if (draggingRutinaId) addRutinaToFolder(draggingRutinaId, f.id); setDragOverFolderId(null); setDraggingRutinaId(null); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      activeCarpeta === f.id ? 'text-white border' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                    } ${dragOverFolderId === f.id ? 'ring-2 ring-gold scale-110 bg-gold/20 !border-gold' : ''}`}
                    style={activeCarpeta === f.id && dragOverFolderId !== f.id ? { background: `${f.color}22`, borderColor: `${f.color}66`, color: f.color } : {}}
                    data-testid={`folder-${f.id}`}
                  >
                    <FolderOpen className="w-3 h-3" />
                    {editingFolder === f.id ? (
                      <input autoFocus value={f.nombre}
                        onChange={e => setCarpetas(prev => prev.map(c => c.id === f.id ? { ...c, nombre: e.target.value } : c))}
                        onBlur={() => renameFolder(f.id, f.nombre)}
                        onKeyDown={e => e.key === 'Enter' && renameFolder(f.id, f.nombre)}
                        className="bg-transparent border-none outline-none text-xs w-20"
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span>{f.nombre} ({rutinas.filter(r => (r.carpetas || []).includes(f.id)).length})</span>
                    )}
                    <span onClick={e => { e.stopPropagation(); setFolderMenuId(folderMenuId === f.id ? null : f.id); }}
                      className="text-gray-500 hover:text-white ml-0.5 cursor-pointer" data-testid={`folder-menu-${f.id}`}>
                      <MoreVertical className="w-3 h-3" />
                    </span>
                  </button>
                  {folderMenuId === f.id && (
                    <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 z-50 min-w-[120px]">
                      <button onClick={() => { setEditingFolder(f.id); setFolderMenuId(null); }}
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 flex items-center gap-2">
                        <Edit className="w-3 h-3" /> Renombrar
                      </button>
                      <button onClick={() => deleteFolder(f.id)}
                        className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-gray-800 flex items-center gap-2">
                        <Trash2 className="w-3 h-3" /> Eliminar
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {showNewFolder ? (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createFolder()}
                    placeholder="Nombre..."
                    className="bg-gray-800 border border-gray-600 rounded-lg px-2 py-1 text-xs text-white w-24 outline-none focus:border-gold"
                    data-testid="input-new-folder"
                  />
                  <button onClick={createFolder} className="text-green-400 hover:text-green-300 p-1" data-testid="btn-save-folder">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="text-gray-500 hover:text-white p-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowNewFolder(true)}
                  className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gold bg-gray-800 border border-dashed border-gray-700 hover:border-gold/50 flex items-center gap-1 whitespace-nowrap transition-colors flex-shrink-0"
                  data-testid="btn-new-folder">
                  <FolderPlus className="w-3 h-3" /> Nueva
                </button>
              )}
            </div>

            {/* Routine list (filtered) */}
            <div className="space-y-2">
              {filteredRutinas.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm bg-gray-900/40 rounded-xl border border-gray-800">
                  {activeCarpeta === 'sin_clasificar' ? 'Todas las rutinas estan organizadas' : 'No hay rutinas en esta carpeta'}
                </div>
              ) : filteredRutinas.map(r => {
                const c = cfg(r.tipo);
                const lastW = historial.find(h => h.rutina_id === r.id);
                const rutinaFolders = (r.carpetas || []).map(fid => carpetas.find(f => f.id === fid)).filter(Boolean);
                return (
                  <div key={r.id}
                    draggable={carpetas.length > 0}
                    onDragStart={(e) => { setDraggingRutinaId(r.id); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragEnd={() => { setDraggingRutinaId(null); setDragOverFolderId(null); }}
                    className={`bg-gray-900 rounded-xl border border-gray-800 p-3 transition-all ${
                      carpetas.length > 0 ? 'cursor-grab active:cursor-grabbing' : ''
                    } ${draggingRutinaId === r.id ? 'opacity-40 scale-95' : ''}`}
                    data-testid={`rutina-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0 ${c.accent}`}>{c.icon}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm truncate">{r.nombre}</p>
                        <p className="text-gray-500 text-xs">{r.ejercicios.length} ejercicios &middot; {c.label}
                          {lastW && <> &middot; <span className="text-gray-400">{new Date(lastW.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span></>}
                        </p>
                      </div>
                      <button onClick={() => startEntrenar(r)} className="bg-gold/20 text-gold p-2 rounded-lg hover:bg-gold/30 transition-colors" data-testid={`btn-train-${r.id}`}>
                        <Play className="w-4 h-4" />
                      </button>
                      {isEntrenador && (
                        <button onClick={() => openAsignar(r)} className="text-cyan-500 hover:text-cyan-400 p-2" title="Asignar a clientes" data-testid={`btn-asignar-${r.id}`}>
                          <Users className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setManageFoldersRutina(manageFoldersRutina === r.id ? null : r.id)} className="text-gray-600 hover:text-gold p-2" title="Organizar en carpetas" data-testid={`btn-folders-${r.id}`}>
                        <FolderOpen className="w-4 h-4" />
                      </button>
                      <button onClick={() => startEditRutina(r)} className="text-gray-600 hover:text-white p-2" data-testid={`btn-edit-${r.id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteRutina(r.id)} className="text-gray-600 hover:text-red-400 p-2" data-testid={`btn-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Folder tags */}
                    {rutinaFolders.length > 0 && (
                      <div className="flex gap-1 mt-2 ml-13 flex-wrap">
                        {rutinaFolders.map(f => (
                          <span key={f.id} className="text-[10px] px-1.5 py-0.5 rounded-md" style={{ background: `${f.color}22`, color: f.color, border: `1px solid ${f.color}33` }}>
                            {f.nombre}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Folder assignment dropdown */}
                    {manageFoldersRutina === r.id && carpetas.length > 0 && (
                      <div className="mt-2 ml-13 p-2 bg-gray-800 rounded-lg border border-gray-700" data-testid={`folder-manage-${r.id}`}>
                        <p className="text-[10px] text-gray-400 uppercase mb-1.5">Carpetas:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {carpetas.map(f => {
                            const isIn = (r.carpetas || []).includes(f.id);
                            return (
                              <button key={f.id} onClick={() => toggleRutinaFolder(r.id, f.id)}
                                className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 transition-colors ${
                                  isIn ? 'text-white' : 'text-gray-500 hover:text-white bg-gray-700'
                                }`}
                                style={isIn ? { background: `${f.color}33`, color: f.color, border: `1px solid ${f.color}55` } : {}}
                                data-testid={`toggle-folder-${r.id}-${f.id}`}
                              >
                                {isIn ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {f.nombre}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {manageFoldersRutina === r.id && carpetas.length === 0 && (
                      <div className="mt-2 ml-13 p-2 bg-gray-800 rounded-lg border border-gray-700 text-xs text-gray-500">
                        Crea carpetas primero con el boton "+ Nueva" arriba
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Historial + Progresion */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setView('historial')}
            className="py-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 text-gray-400 hover:text-gold flex items-center justify-center gap-2 transition-colors"
            data-testid="btn-historial">
            <Calendar className="w-5 h-5" />
            <span className="font-medium text-sm">Historial</span>
          </button>
          <button onClick={openProgresion}
            className="py-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 text-gray-400 hover:text-gold flex items-center justify-center gap-2 transition-colors"
            data-testid="btn-progresion">
            <TrendingUp className="w-5 h-5" />
            <span className="font-medium text-sm">Progresion</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default SesionesFuerza;
